import { PropsWithChildren } from 'react';

import {
  Button,
  Icons,
  Skeleton,
  Typography,
  Box,
  Stack,
  colorOrToken,
  tokens,
} from '@cast/design-system';
import { PermissionsCenterFeature } from '@cast/types-permissions-center';

import { Link } from 'components/router';
import { createPermissionsCenterUrl } from 'features-op/permissions-center/navigation';
import {
  AllFeatureTypes as PermissionsCenterFeatureType,
  SupportedCloudProvider as PermissionsCenterSupportedCloudProvider,
} from 'features-op/permissions-center/types';
import { getTotalPermissionsCount } from 'features-op/permissions-center/utils';

const Container = ({ children }: PropsWithChildren) => {
  return (
    <Box
      data-testid="permissions-summary"
      sx={{
        bgcolor: colorOrToken('blue.50', tokens.theme.bg.status.infoSubtle),
        border: '1px solid',
        borderColor: colorOrToken(
          'blue.100',
          tokens.theme.border.status.infoSubtle
        ),
        borderRadius: '0 0 4px 4px',
        px: 12,
        py: 8,
      }}
    >
      {children}
    </Box>
  );
};

type LoadableProps =
  | {
      isLoading: true;
      permissionsCenterFeatures: undefined;
    }
  | {
      isLoading: false;
      permissionsCenterFeatures: PermissionsCenterFeature[];
    };

type Props = {
  cloudProvider: PermissionsCenterSupportedCloudProvider;
  selectedPermissionsCenterFeatures: PermissionsCenterFeatureType[];
} & LoadableProps;

export const PermissionsSummaryContent = ({
  cloudProvider,
  permissionsCenterFeatures,
  selectedPermissionsCenterFeatures,
  isLoading,
}: Props) => {
  return (
    <Container>
      <Stack gap={8}>
        <Stack direction="row" gap={4} alignItems="baseline">
          <Typography variant="P12M" color="textSecondary">
            Cloud permissions:
          </Typography>
          <Typography variant="A2" data-testid="permissions-summary-count">
            {isLoading ? (
              <Skeleton
                width={30}
                variant="text"
                data-testid="permissions-summary-loading"
              />
            ) : (
              getTotalPermissionsCount({
                features: permissionsCenterFeatures,
                selectedFeatures: selectedPermissionsCenterFeatures,
              })
            )}
          </Typography>
        </Stack>

        <Stack direction="row" gap={4} alignItems="baseline">
          <Typography variant="P12R" color="textSecondary">
            View all or export summary from
          </Typography>
          <Button
            testId="permissions-summary-link"
            variant="text"
            component={Link}
            endIcon={<Icons.ArrowSquareOut size={12} />}
            to={createPermissionsCenterUrl({
              cloudProvider,
              selectedFeatures: selectedPermissionsCenterFeatures,
            })}
            target="_blank"
          >
            Permission center
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
};

type PermissionsSummaryContentErrorProps = {
  refetch: () => void;
};

const PermissionsSummaryContentError = ({
  refetch,
}: PermissionsSummaryContentErrorProps) => {
  return (
    <Container>
      <Stack
        data-testid="permissions-summary-error"
        direction="row"
        justifyContent="space-between"
      >
        <Stack direction="row" alignItems="center" gap={8}>
          <Icons.Warning
            css={() => ({
              color: colorOrToken('red.500', tokens.theme.fg.status.error),
            })}
            weight="fill"
            size={20}
          />
          <Typography variant="P12R">
            There was an issue loading the permissions summary
          </Typography>
        </Stack>

        <Button variant="text" onClick={refetch}>
          Refetch
        </Button>
      </Stack>
    </Container>
  );
};

PermissionsSummaryContent.Error = PermissionsSummaryContentError;
